import React, {
  FocusEvent,
  FocusEventHandler,
  useMemo,
  useRef,
} from 'react';
import AutoCompletedInput from 'core/components/AutoComplete';
import {
  TimeOption,
  formatToReadableTime,
  parseMinutesFromInput,
  toMinutes,
  generateTimes,
  filterTimeOptions,
  formatDuration,
} from 'core/components/DateTimePicker/TimePicker/timepickerUtils';

type Props = {
  value: string;
  onChange: (value: string) => void;
  minTime?: string | number;
  maxTime?: string | number;
  className?: string;
  stepMinutes?: number;
  startTime?: string;
};

export default function TimePicker({
  value,
  onChange,
  minTime,
  maxTime,
  stepMinutes = 15,
  className,
  startTime,
}: Props) {
  const startMinutes = useMemo(() => toMinutes(startTime), [startTime]);
  const minMinutes = useMemo(() => toMinutes(minTime), [minTime]);
  const maxMinutes = useMemo(() => toMinutes(maxTime), [maxTime]);

  const allOptions = useMemo<TimeOption[]>(
    () => generateTimes(Math.max(1, Math.floor(stepMinutes))),
    [stepMinutes],
  );

  const filteredOptions = useMemo<TimeOption[]>(
    () =>
      allOptions.filter(({ minutes }) =>
        (startMinutes === undefined || minutes > startMinutes)
      && (minMinutes === undefined || minutes >= minMinutes)
      && (maxMinutes === undefined || minutes <= maxMinutes)),
    [allOptions, startMinutes, minMinutes, maxMinutes],
  );

  const lastValidRef = useRef<string | null>(value);

  // DO NOT normalize while typing
  const handleChange = (newValue: string | TimeOption | null) => {
    if (newValue && typeof newValue === 'object') {
      // Selecting an option: emit canonical "HH:MM am/pm"
      onChange(newValue.label);
    } else if (typeof newValue === 'string') {
      onChange(newValue);
    }
  };

  // Normalize on blur (parse → format with space)
  const handleBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    const raw = (e?.currentTarget?.value ?? value) as string;
    const mins = parseMinutesFromInput(raw);
    if (mins !== -1) {
      lastValidRef.current = formatToReadableTime(mins);
    }
    onChange(lastValidRef.current ?? '');
  };

  const onSelect = (newValue: TimeOption | TimeOption[]) => {
    const timeOption = newValue as TimeOption;
    lastValidRef.current = timeOption.label;

    handleChange(timeOption);
  };

  return (
    <AutoCompletedInput<TimeOption>
      freeSolo
      includeInputInList
      selectOnFocus
      filterSelectedOptions={false}
      options={filteredOptions}
      filterOptions={filterTimeOptions}
      value={null}
      inputValue={value}
      onBlur={(e) => {
        handleBlur(e as FocusEvent<HTMLInputElement | HTMLTextAreaElement>);
      }}
      onInputChange={(e, newValue) => { handleChange(newValue); }}
      onChange={onSelect}
      getLabel={(option: TimeOption) => option?.label ?? option}
      renderOption={(props, option) => {
        const showDelta = startMinutes !== undefined;
        const delta = showDelta ? option.minutes - (startMinutes as number) : 0;
        const extra = showDelta && delta > 0 ? ` (${formatDuration(delta)})` : '';
        return (
          <li {...props} key={`${option.label}-${option.minutes}`}>
            {option.label}
            {extra}
          </li>
        );
      }}
      className={className}
      dropdownClassName="time-picker-autocomplete"
      disableClearable
      openOnFocus
      placeholder="02:00 pm • 2p • 1400"
    />
  );
}
